import { Fragment, type ReactNode, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { STATES_QUERY_PARAM } from "../consts";
import {
  buildGroupQueryKey,
  buildGroupSelectionToken,
  setParamValues,
} from "../helpers";
import { StateGroup, StateOption } from "../types";

import { AccordionSection } from "./AccordionSection";

type StateSection =
  | { type: "group"; group: StateGroup }
  | { type: "accordion"; label: string; groups: StateGroup[] };

type StateBlock =
  | { type: "plain"; states: StateOption[] }
  | { type: "section"; label: string; states: StateOption[] };

const buildSections = (stateGroups: StateGroup[]): StateSection[] => {
  const sections: StateSection[] = [];

  for (const group of stateGroups) {
    const accordionLabel = group.accordion;
    if (!accordionLabel) {
      sections.push({ type: "group", group });
      continue;
    }

    const last = sections[sections.length - 1];
    if (last?.type === "accordion" && last.label === accordionLabel) {
      last.groups.push(group);
      continue;
    }

    sections.push({
      type: "accordion",
      label: accordionLabel,
      groups: [group],
    });
  }

  return sections;
};

/** Groups consecutive states that share a `section` label into accordion blocks. */
const buildStateBlocks = (states: StateOption[]): StateBlock[] => {
  const blocks: StateBlock[] = [];

  for (const state of states) {
    const sectionLabel = state.section;
    const last = blocks[blocks.length - 1];

    if (!sectionLabel) {
      if (last?.type === "plain") {
        last.states.push(state);
      } else {
        blocks.push({ type: "plain", states: [state] });
      }
      continue;
    }

    if (last?.type === "section" && last.label === sectionLabel) {
      last.states.push(state);
      continue;
    }

    blocks.push({ type: "section", label: sectionLabel, states: [state] });
  }

  return blocks;
};

/**
 * A small "?" affordance that reveals `text` in a tooltip bubble on hover,
 * so others loading up the prototype can see what a state toggle does.
 */
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="group relative inline-flex shrink-0">
    <span
      className="flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-[#555] text-[9px] leading-none text-[#999] normal-case transition-colors group-hover:border-[#888] group-hover:text-[#ddd]"
      tabIndex={0}
      role="button"
      aria-label={text}
    >
      ?
    </span>
    <span className="pointer-events-none absolute top-full left-1/2 z-10 mt-1.5 w-52 -translate-x-1/2 rounded-md bg-[#2a2a2a] px-2.5 py-1.5 text-[11px] leading-snug font-normal text-[#ddd] normal-case opacity-0 shadow-[0_2px_8px_rgba(0,0,0,0.3)] transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
      {text}
    </span>
  </span>
);

/**
 * Renders a `stateGroups` entry as a horizontal slider instead of a radio
 * list. Position is index-based (not `stateNumber`-based) so the states
 * don't need to be contiguous integers.
 */
const SliderStateOptions: React.FC<{
  group: StateGroup;
  updateState: (override: { groupKey: string; value: number }) => void;
}> = ({ group, updateState }) => {
  const activeIndex = Math.max(
    0,
    group.states.findIndex(
      (stateOption) => stateOption.stateNumber === group.active
    )
  );
  const activeLabel = group.states[activeIndex]?.label ?? "";
  const notchesListId = `slider-notches-${buildGroupQueryKey(group)}`;

  return (
    <div className="flex items-center gap-2 px-0.5 py-1">
      <input
        type="range"
        min={0}
        max={group.states.length - 1}
        step={1}
        value={activeIndex}
        list={group.showNotches ? notchesListId : undefined}
        onChange={(event) => {
          const nextOption = group.states[Number(event.target.value)];
          if (!nextOption) return;
          const groupKey = buildGroupQueryKey(group);
          group.onChange(nextOption.stateNumber);
          updateState({ groupKey, value: nextOption.stateNumber });
        }}
        className="h-1 flex-1 cursor-pointer accent-[#D67CFF]"
      />
      {group.showNotches ? (
        <datalist id={notchesListId}>
          {group.states.map((_, index) => (
            <option key={index} value={index} />
          ))}
        </datalist>
      ) : null}
      <span className="text-fs-text-primary-inverse w-6 shrink-0 text-right text-[12px] font-medium">
        {activeLabel}
      </span>
    </div>
  );
};

/**
 * Renders a `stateGroups` entry as a segmented "split button" instead of a
 * radio list — adjacent buttons forming one pill (one per state), with the
 * active option highlighted.
 */
const SplitButtonStateOptions: React.FC<{
  group: StateGroup;
  updateState: (override: { groupKey: string; value: number }) => void;
}> = ({ group, updateState }) => (
  <div
    className="grid gap-[2px] rounded-lg bg-[#1f1f1f] p-[2px]"
    style={{
      gridTemplateColumns: `repeat(${group.states.length}, minmax(0, 1fr))`,
    }}
  >
    {group.states.map((stateOption) => {
      const isActive = stateOption.stateNumber === group.active;
      return (
        <button
          key={stateOption.stateNumber}
          onClick={() => {
            const groupKey = buildGroupQueryKey(group);
            group.onChange(stateOption.stateNumber);
            updateState({ groupKey, value: stateOption.stateNumber });
          }}
          className={`rounded-md px-2.5 py-1.5 text-[12px] transition-colors ${
            isActive
              ? "text-fs-text-primary-inverse bg-[#D67CFF]/20 font-medium"
              : "text-[#999] hover:text-[#ddd]"
          }`}
        >
          {stateOption.label}
        </button>
      );
    })}
  </div>
);

/**
 * Renders a `stateGroups` entry as a radio list, tucking any states that carry
 * a `section` label into a collapsible block (e.g. archived options).
 */
const RadioStateOptions: React.FC<{
  group: StateGroup;
  updateState: (override: { groupKey: string; value: number }) => void;
}> = ({ group, updateState }) => {
  const renderButton = (stateOption: StateOption) => {
    const isActive = stateOption.stateNumber === group.active;
    return (
      <button
        onClick={() => {
          const groupKey = buildGroupQueryKey(group);
          group.onChange(stateOption.stateNumber);
          updateState({ groupKey, value: stateOption.stateNumber });
        }}
        className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-1.5 text-left text-[12px] leading-snug transition-colors ${
          isActive
            ? "text-fs-text-primary-inverse bg-[#D67CFF]/10 font-medium"
            : "text-[#ddd] hover:bg-[#1f1f1f]"
        }`}
      >
        <span
          className={`mt-[5px] inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
            isActive ? "bg-[#D67CFF]" : "bg-[#555]"
          }`}
        />
        <span className="min-w-0">{stateOption.label}</span>
      </button>
    );
  };

  const renderOption = (stateOption: StateOption) => (
    <Fragment key={stateOption.stateNumber}>
      {renderButton(stateOption)}
      {stateOption.contentAfter}
    </Fragment>
  );

  return (
    <div className="space-y-2 py-0.5">
      {buildStateBlocks(group.states).map((block) => {
        if (block.type === "plain") {
          return (
            <div key={block.states.map((state) => state.stateNumber).join("-")}>
              {block.states.map(renderOption)}
            </div>
          );
        }

        return (
          <AccordionSection
            key={block.label}
            label={block.label}
            defaultOpen={block.states.some(
              (state) => state.stateNumber === group.active
            )}
          >
            <div>{block.states.map(renderOption)}</div>
          </AccordionSection>
        );
      })}
    </div>
  );
};

const renderGroupControls = (
  group: StateGroup,
  updateState: (override: { groupKey: string; value: number }) => void
) => (
  <div>
    <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-medium tracking-wide text-[#aaa] uppercase">
      {group.title}
      {group.description ? <InfoTooltip text={group.description} /> : null}
    </div>
    {group.sliderMode && group.states.length > 1 ? (
      <SliderStateOptions group={group} updateState={updateState} />
    ) : group.splitButtonMode && group.states.length >= 2 ? (
      <SplitButtonStateOptions group={group} updateState={updateState} />
    ) : (
      <RadioStateOptions group={group} updateState={updateState} />
    )}
    {group.customContent ? (
      <div className="mt-2">{group.customContent}</div>
    ) : null}
  </div>
);

const sectionTitleFor = (section: StateSection): string | undefined =>
  section.type === "group"
    ? section.group.sectionTitle
    : section.groups[0]?.sectionTitle;

const renderStateSections = (
  sections: StateSection[],
  updateState: (override: { groupKey: string; value: number }) => void
): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastSectionTitle: string | undefined;

  sections.forEach((section) => {
    const sectionTitle = sectionTitleFor(section);
    if (sectionTitle && sectionTitle !== lastSectionTitle) {
      nodes.push(
        <div
          key={`section-title-${sectionTitle}`}
          className="text-[10px] font-medium tracking-wide text-[#aaa] uppercase"
        >
          {sectionTitle}
        </div>
      );
    }
    lastSectionTitle = sectionTitle;

    if (section.type === "group") {
      nodes.push(
        <Fragment key={section.group.queryKey ?? section.group.title}>
          {renderGroupControls(section.group, updateState)}
          {section.group.dividerAfter ? (
            <div className="border-t border-[#2a2a2a]" />
          ) : null}
        </Fragment>
      );
      return;
    }

    const accordionHasDivider = section.groups.some(
      (group) => group.dividerAfter
    );

    nodes.push(
      <Fragment key={section.label}>
        <AccordionSection label={section.label}>
          {section.groups.map((group) => (
            <div key={group.queryKey ?? group.title}>
              {renderGroupControls(group, updateState)}
            </div>
          ))}
        </AccordionSection>
        {accordionHasDivider ? (
          <div className="border-t border-[#2a2a2a]" />
        ) : null}
      </Fragment>
    );
  });

  return nodes;
};

export const StatesPanel: React.FC<{ stateGroups: StateGroup[] }> = ({
  stateGroups,
}) => {
  const history = useHistory();
  const sections = useMemo(() => buildSections(stateGroups), [stateGroups]);

  const buildStateTokens = (override?: {
    groupKey: string;
    value: number;
  }): string[] =>
    stateGroups
      .map((group) => {
        const groupKey = buildGroupQueryKey(group);
        const valueForGroup =
          override?.groupKey === groupKey ? override.value : group.active;
        const hasValue = group.states.some(
          (stateOption) => stateOption.stateNumber === valueForGroup
        );
        if (!hasValue) {
          return null;
        }
        return buildGroupSelectionToken(groupKey, String(valueForGroup));
      })
      .filter((token): token is string => token !== null);

  const updateState = (override: { groupKey: string; value: number }): void => {
    const params = new URLSearchParams(location.search);
    setParamValues(params, STATES_QUERY_PARAM, buildStateTokens(override));
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : undefined,
      hash: location.hash,
    });
  };

  return (
    <div className="w-64 rounded-xl border border-[#2a2a2a] bg-[#141414] shadow-[0_2px_8px_rgba(0,0,0,0.25)]">
      <div className="border-b border-[#2a2a2a] px-3 py-2">
        <span className="text-[11px] font-medium text-[#aaa]">States</span>
      </div>
      {stateGroups.length > 0 ? (
        <div className="space-y-4 p-3">
          {renderStateSections(sections, updateState)}
        </div>
      ) : (
        <div className="p-4">
          <p className="text-[12px] text-[#aaa]">No states yet.</p>
        </div>
      )}
    </div>
  );
};
